import { Button, Col, DatePicker, Form, Row, Select, Input } from "antd";
import moment from "moment";
import { useEffect, useState, useLayoutEffect } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { SLOT_CONFIG } from "../../../common/constants";

const StyledFilterContainer = styled.div`
  &,
  & .filter {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;

    &__item {
      margin: 0.5rem;

      & .ant-form-item {
        margin: 0;
      }

      &--submit {
        align-self: flex-end;
      }
    }
  }
`;

export const AppAvailableOrdersFilter = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [form] = Form.useForm();
    const { darkStores } = useAppSelector((state) => state.adminServices);
    const [isDateRequired, setIsDateRequired] = useState(false);

    // Sync form with searchParams whenever they change
    useEffect(() => {
        const params = Object.fromEntries(new URLSearchParams(searchParams));
        const formValues = {
            deliveryDate: params.deliveryDate ? moment(params.deliveryDate, "YYYY-MM-DD") : undefined,
            slotId: params.slotId || undefined,
            storeId: params.storeId ? parseInt(params.storeId) : undefined,
        };

        form.setFieldsValue(formValues);

        // Set initial required state based on URL params
        setIsDateRequired(!!params.deliveryDate);
    }, [form, searchParams]);

    // Ensure form is synced on mount (before paint) to prevent empty fields on expand
    useLayoutEffect(() => {
        const params = Object.fromEntries(new URLSearchParams(searchParams));
        const formValues = {
            deliveryDate: params.deliveryDate ? moment(params.deliveryDate, "YYYY-MM-DD") : undefined,
            slotId: params.slotId || undefined,
            storeId: params.storeId ? parseInt(params.storeId) : undefined,
        };

        form.setFieldsValue(formValues);
        setIsDateRequired(!!params.deliveryDate);
    }, []);

    const onSearch = async (values: Record<string, any>) => {
        try {
            // Validate form before proceeding
            await form.validateFields();

            const params: Record<string, string> = {};

            // deliveryDate is mandatory
            if (values.deliveryDate) {
                params.deliveryDate = moment(values.deliveryDate).format("YYYY-MM-DD");
            }

            if (values.storeId) {
                params.storeId = String(values.storeId);
            }

            if (values.slotId) {
                params.slotId = String(values.slotId);
            }

            setSearchParams(params);
        } catch (error) {
            // Form validation failed
            console.error('Validation failed:', error);
        }
    };

    const handleDateChange = (value: moment.Moment | null) => {
        // Update the form values
        form.setFieldsValue({ deliveryDate: value });

        // Set required state if date is selected
        setIsDateRequired(!!value);
    };

    const disabledDate = (current: moment.Moment | null) => {
        if (!current) return false;
        // Allow only tomorrow onwards (disable today and past dates)
        const tomorrow = moment().add(1, 'day').startOf('day');
        if (current.isBefore(tomorrow)) return true;

        return false;
    };

    return (
        <StyledFilterContainer>
            <Form
                form={form}
                name="available-orders-filter-form"
                onFinish={onSearch}
                layout="vertical"
                style={{ width: "100%" }}
            >
                <Row className="filter">
                    <Col className="filter__item">
                        <Form.Item
                            name="deliveryDate"
                            label="Delivery Date *"
                            rules={[
                                {
                                    required: isDateRequired,
                                    message: 'Please select Delivery Date'
                                }
                            ]}
                        >
                            <DatePicker
                                format="YYYY-MM-DD"
                                onChange={(val) => handleDateChange(val)}
                                disabledDate={disabledDate}
                                placeholder="Select delivery date"
                                size="small"
                                allowClear
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={20} sm={4} md={5} lg={4} className="filter__item">
                        {darkStores && <Form.Item name="storeId" label="Store ID" required={false} >
                            <Select placeholder="Search by Store ID" size="small" allowClear showSearch onClear={() => {
                                form.submit();
                            }}>
                                {(darkStores).map((e) => (
                                    <Select.Option value={e.id} key={e.id}>
                                        {e.name} || {e.id}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>}
                    </Col>

                    <Col xs={20} sm={4} md={5} lg={4} className="filter__item">
                        <Form.Item name="slotId" label="Slot">
                            <Select
                                placeholder="Select a slot"
                                size="small"
                                allowClear
                                showSearch
                                onClear={() => {
                                    form.submit();
                                }}
                            >
                                {Object.entries(SLOT_CONFIG).map(([id, slotName]) => (
                                    <Select.Option value={parseInt(id)} key={id}>
                                        {id} - {slotName}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col
                        xs={20}
                        sm={4}
                        md={2}
                        lg={2}
                        className="filter__item filter__item--submit"
                    >
                        <Form.Item>
                            <Button type="primary" htmlType="submit" size="small">
                                Search
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </StyledFilterContainer>
    );
};
